<script lang="ts">
import '@banno/jha-wc/src/cards/jha-card/jha-card.js'
import '@banno/jha-wc/src/collapsible-content/jha-collapsible-section/jha-collapsible-section.js'
import '@banno/jha-wc/src/dropdowns/jha-dropdown/jha-dropdown.js'
import '@banno/jha-wc/src/dropdowns/jha-dropdown-menu/jha-dropdown-menu.js'
import '@banno/jha-wc/src/dropdowns/jha-dropdown-menu-item/jha-dropdown-menu-item.js'
import {defineComponent, type PropType} from 'vue'
import {$inj, $injByInterface} from '@/common/decorators/depinject'
import ToastService from '@/common/services/toastFactory/toastFactory'
import CommonService from '@/common/services/commonService'
import type IColourSchemeUtils from '@/common/services/IColorSchemeUtil'
import {BltSideNavConstant} from '@/common/constant/bltSideNavConstant'
import BltChangePassword from '@/common/components/bltChangePassword/bltChangePassword.vue'
import BltAnimatedChevron from '@/common/components/bltAnimatedChevron/bltAnimatedChevron.vue'
import '@banno/jha-wc/src/jha-accordion/jha-accordion.js'
import LoggedInUserDetails from '@/common/components/bltSideNav/LoggedInUserDetails.vue'
import {openAnywhereSites, type Site} from '@/common/components/bltSideNav/openAnywhereSitesEnum'
import {mapState} from 'pinia'
import {useLocalStorage} from '@/common/stores/LocalStorageStore'
import {type Module} from '@/common/components/bltSideNav/managementAppsEnum'
import {useRoute, useRouter} from 'vue-router'
import {BltSidenavStore} from '@/branchmanager/stores/bltSidenavStore'
import type IFeature from '@/common/models/IFeature'
import {FeatureCodes} from '@/branchmanager/app/enums/featureCodesEnum'
import '@banno/jha-wc/src/jha-nav/jha-nav.js'
import '@banno/jha-wc/src/icons/jha-icon-settings.js'
import '@banno/jha-wc/src/icons/jha-icon-lock.js'
import '@banno/jha-wc/src/icons/jha-icon-logout.js'
import {VsgDropdownToggle} from '@jack-henry/jha-vue-components'
import BltManagementModuleButtons from '@/common/components/bltSideNav/bltManagementModuleButtons.vue'
import {useLDFlag} from 'launchdarkly-vue-client-sdk'

export default defineComponent({
  name: 'bltSideNav',
  components: {
    BltManagementModuleButtons,
    VsgDropdownToggle,
    LoggedInUserDetails,
    BltChangePassword,
    BltAnimatedChevron
  },
  props: {
    activeFeatures: {
      type: Object as PropType<Array<IFeature>>,
      required: true
    }
  },
  setup() {
    const commonService = $inj(CommonService)
    const colorSchemeUtil = $injByInterface<IColourSchemeUtils>('IColourSchemeUtils')
    const toastService = $inj(ToastService)
    const bltSidenavStore = BltSidenavStore()
    const router = useRouter()
    const route = useRoute()
    const oaLogoRedesign = useLDFlag('oa-logo-redesign', false)
    return {
      commonService,
      colorSchemeUtil,
      router,
      toastService,
      route,
      bltSidenavStore,
      oaLogoRedesign
    }
  },
  data() {
    return {
      navOptions: openAnywhereSites as Site[],
      logoBase64: null as string | null,
      fiName: null as string | null,
      institutionConfig: {} as any,
      openDropDown: false,
      showChangePasswordDialog: false,
      BltSideNavConstant,
      touchstartX: 0,
      touchendX: 0,
    }
  },
  created() {
    if (!this.oaLogoRedesign) {
      this.institutionConfig = (window as any).institutionConfig
      this.fiName = this.institutionConfig.name
      this.logoBase64 = this.colorSchemeUtil.onColorSchemeChange()
    }
  },
  emits: ['hideSidebar', 'showSidebar'],
  methods: {
    routeIsActive(route: string) {
      return this.route.matched.map((route) => route.name).includes(route)
    },
    isModule(module: Module) {
      return 'route' in module
    },
    showChangePasswordModal(): void {
      this.showChangePasswordDialog = true
    },
    handleManagementModuleClick(route: string): void {
      this.router.push({
        name: route
      })
      this.$emit('hideSidebar')
    },
    async logout(): Promise<void> {
      await this.commonService.logout()
      this.$emit('hideSidebar')
    }
  },
  computed: {
    openMgmtSection() {
      // If you're in an admin/* state...
      return this.route.matched.map((route) => route.name).includes('ADMIN')
    },
    Module() {
      return Module
    },
    ...mapState(useLocalStorage, ['login']),
    FeatureCodes() {
      return FeatureCodes
    },
    isJhidEnabled(): boolean {
      return window.JHID_ENABLED
    }
  }
})
</script>

<template>
  <div
    class="sidebar-overlay"
    @click="$emit('hideSidebar')"></div>
  <jha-card
    class="left-container-card left-sidebar"
    :class="{'logo-redesign-sidebar': oaLogoRedesign}"
    ref="sideNav">

    <template v-if="!oaLogoRedesign">
      <div class="logo">
        <img
          :src="logoBase64"
          :alt="fiName"
          class="logo-img" />
      </div>

      <div class="divider"></div>
    </template>

    <div class="nav-items">
      <jha-nav class="side-nav-jha-nav">
        <a
          v-for="{name, route, icon} in navOptions.filter((option) =>
            activeFeatures.some((feature) => feature.featureCode === option.featureCode)
          )"
          :key="name"
          :class="routeIsActive(route) ? 'active' : 'nonActive'"
          :active="routeIsActive(route) ? 'true' : null"
          @click="handleManagementModuleClick(route)"
          tabindex="0"
          role="button"
          @keydown.enter="handleManagementModuleClick(route)">
          <span v-html="icon"></span>
          {{ name }}
        </a>
      </jha-nav>

      <jha-collapsible-section
        :isOpen="openMgmtSection"
        v-if="activeFeatures.some((feature) => feature.featureCode === FeatureCodes.MANAGEMENT_CONSOLE)"
        class="management-collapsible-section">
        <div slot="header">
          <jha-icon-settings
            style="margin-left: 11px"
            class="nav-icon" />
          <span style="font-weight: 400; font-size: 16px; line-height: 24px">Management Console</span>
        </div>
        <div slot="body">

          <blt-management-module-buttons @click="handleManagementModuleClick($event)"
                                         :activeFeatures/>

        </div>
      </jha-collapsible-section>
    </div>

    <div class="action-items">
      <jha-dropdown class="side-nav-profile-dropdown-menu">
        <vsg-dropdown-toggle
          slot="toggle"
          class="toggle-divider"
          @click.stop="openDropDown = !openDropDown"
          @keydown.enter="openDropDown = !openDropDown">
          <div class="side-nav-dropdown-left">
            <LoggedInUserDetails />
            <blt-animated-chevron :on="openDropDown"></blt-animated-chevron>
          </div>
        </vsg-dropdown-toggle>
        <jha-dropdown-menu
          open-up=""
          class="side-nav-profile-dropdown-menu-width">
          <jha-dropdown-menu-item
            v-if="!isJhidEnabled"
            @click.stop="showChangePasswordModal()"
            @keydown.enter="showChangePasswordModal()">
            <a>
              <jha-icon-lock class="icon-pos"></jha-icon-lock>
              {{ BltSideNavConstant.CHANGE_PASSWORD }}</a
            >
          </jha-dropdown-menu-item>
          <jha-dropdown-menu-item
            @click.stop="logout"
            @keydown.enter="logout">
            <a>
              <jha-icon-logout class="icon-pos"></jha-icon-logout>
              {{ BltSideNavConstant.SIGN_OUT }}</a
            >
          </jha-dropdown-menu-item>
        </jha-dropdown-menu>
      </jha-dropdown>
    </div>
  </jha-card>
  <BltChangePassword
    v-if="showChangePasswordDialog"
    :login="login ?? ''"
    @close-dialog="showChangePasswordDialog = false" />
</template>

<style scoped>
.management-collapsible-section {
  --jha-accordion-open-body-padding-top: 0;
  --jha-background-color: transparent;
}

.management-collapsible-section:not([is-open]):hover {
  --jha-background-color: rgb(230, 230, 230);
}

jha-nav.new {
  position: relative;
}

.left-sidebar {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  border-radius: 0;
  z-index: 99;
  width: 272px;
  box-shadow: var(--jha-card-shadow, 0 1px 1px 0 rgba(0, 0, 0, 0.24), 0 1px 4px rgba(0, 0, 0, 0.12));
  background-color: var(--jha-card-background-color, var(--jha-component-background-color, #ffffff));
  margin: 0;
  padding-left: 0;
}

.logo-redesign-sidebar {
  height: calc(100dvh - var(--jh-logo-banner-height));
}

.logo {
  margin: var(--jh-space-500) auto;
  text-align: center;
}

.logo .logo-img {
  width: 140px;
  height: auto;
}

.nav-items {
  margin-top: var(--jh-space-200);
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
}

.action-items {
  display: flex;
  flex-direction: column;
}

:deep(.nav-icon) {
  margin-right: 16px;
  padding: 0px;
  vertical-align: middle;
}

.nonActive :deep(.nav-icon) {
  fill: var(--jha-text-base);
}

.active :deep(.nav-icon) {
  fill: var(--primary-button-color);
}

.divider {
  margin-left: 0;
}

.toggle-divider {
  border-top: 1px solid var(--jha-border-color, #e4e7ea);
}

.side-nav-profile-dropdown-menu {
  margin-left: 0px;
}

.side-nav-dropdown-left {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 var(--jh-space-400);
}

.side-nav-jha-nav a {
  cursor: pointer;
  padding: var(--jh-space-300) var(--jh-space-500);
  font-size: 16px;
  line-height: 24px;
}

:deep(.active) {
  color: var(--jha--menuTextSelectedColor);
  font-weight: 500;
}

:deep(.nonActive) {
  font-weight: 400;
}

.icon-pos,
.secondary-fill-icon {
  fill: var(--jha-text-base);
}

.icon-pos {
  padding-right: var(--jh-space-400);
}

jha-dropdown {
  width: 272px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
}

.side-nav-profile-dropdown-menu-width {
  width: 272px;
}

.side-nav-profile-dropdown-menu-width a {
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  padding: var(--jh-space-400) var(--jh-space-500);
  color: var(--jha-text-dark);
}
</style>
